import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Scale, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import Navbar from '@/components/Navbar';
import { logActivity } from '@/lib/activityLog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function WeightLog({ user, onLogout }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weight, setWeight] = useState('');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [initialWeight, setInitialWeight] = useState(null);
  const [latestWeight, setLatestWeight] = useState(null);

  useEffect(() => {
    logActivity('progress_viewed');
    fetchLogs();
    fetchInitialWeight();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/weight-logs/my-logs`);
      const logsData = response.data || [];
      setLogs(logsData);
      
      // Calculate latest weight from logs
      if (logsData.length > 0) {
        // Logs are sorted by date descending, so first is latest
        setLatestWeight(logsData[0].weight);
      }
    } catch (error) {
      console.error('Failed to load weight logs:', error);
      setLogs([]);
      toast.error('Failed to load weight logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchInitialWeight = async () => {
    try {
      const response = await axios.get(`${API}/onboarding/answers`);
      if (response.data?.current_weight) {
        setInitialWeight(response.data.current_weight);
      }
    } catch (error) {
      console.error('Failed to load initial weight:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Accept both "83.4" and "83,4" — normalize comma to period
    const normalized = (weight || '').toString().replace(',', '.').trim();
    const parsed = parseFloat(normalized);
    if (!normalized || isNaN(parsed) || parsed <= 0) {
      toast.error('Please enter a valid weight');
      return;
    }

    try {
      await axios.post(`${API}/weight-logs`, {
        date,
        weight: parsed
      });
      
      toast.success('Weight logged successfully!');
      setWeight('');
      setDate(new Date().toISOString().split('T')[0]);
      fetchLogs();
    } catch (error) {
      console.error('Failed to log weight:', error);
      toast.error(error.response?.data?.detail || 'Failed to log weight');
    }
  };

  if (loading) {
    return <div data-testid="loading">Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-pink-50">
      <Navbar user={user} onLogout={onLogout} />
      
      <div className="page-container max-w-4xl mx-auto">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2" data-testid="weight-log-title">
            Daily Weight Log
          </h1>
          <p className="text-lg text-gray-600">
            Track your weight daily for best results
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Log Weight Form */}
          <Card className="border-0 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-[#1a61e8] to-[#4a8ff5] text-white rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-2xl">
                <Scale className="h-5 w-5 text-white" />
                Log Today's Weight
              </CardTitle>
              <CardDescription className="text-blue-100">Track your progress regularly for best results</CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="date">Date</Label>
                  <Input
                    id="date"
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    required
                    data-testid="date-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    type="text"
                    inputMode="decimal"
                    pattern="[0-9]+([.,][0-9]+)?"
                    placeholder={latestWeight ? `Last: ${latestWeight} kg` : initialWeight ? `${initialWeight}` : "e.g. 83.4 or 83,4"}
                    value={weight}
                    onChange={(e) => {
                      // Allow only digits, one decimal separator (. or ,), max one
                      const v = e.target.value.replace(/[^0-9.,]/g, '');
                      const firstSep = v.search(/[.,]/);
                      const cleaned = firstSep === -1
                        ? v
                        : v.slice(0, firstSep + 1) + v.slice(firstSep + 1).replace(/[.,]/g, '');
                      setWeight(cleaned);
                    }}
                    required
                    data-testid="weight-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-[#1a61e8] hover:bg-[#1552c7] h-12 text-base"
                  disabled={!weight || !date}
                  data-testid="log-weight-button"
                >
                  <Scale className="h-4 w-4 mr-2" />
                  Log Weight
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Recent Logs */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5 text-purple-600" />
                Recent Entries
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="recent-logs">
                {!logs || logs.length === 0 ? (
                  <p className="text-gray-500 text-center py-8">No weight logs yet</p>
                ) : (
                  logs.slice(0, 10).map((log, index) => (
                    <div
                      key={log.id}
                      className="flex justify-between items-center p-3 bg-gray-50 rounded-lg"
                      data-testid={`log-entry-${index}`}
                    >
                      <div>
                        <p className="font-semibold">{log.date}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(log.date).toLocaleDateString('en-US', { weekday: 'long' })}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-600">{log.weight}</p>
                        <p className="text-sm text-gray-600">kg</p>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Weight History Table */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Complete Weight History</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4">Date</th>
                    <th className="text-left py-3 px-4">Day</th>
                    <th className="text-right py-3 px-4">Weight (kg)</th>
                    <th className="text-right py-3 px-4">Change</th>
                  </tr>
                </thead>
                <tbody data-testid="weight-history-table">
                  {logs.map((log, index) => {
                    const prevWeight = logs[index + 1]?.weight;
                    const change = prevWeight ? (log.weight - prevWeight).toFixed(1) : null;
                    
                    return (
                      <tr key={log.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">{log.date}</td>
                        <td className="py-3 px-4">
                          {new Date(log.date).toLocaleDateString('en-US', { weekday: 'long' })}
                        </td>
                        <td className="py-3 px-4 text-right font-semibold">{log.weight}</td>
                        <td className="py-3 px-4 text-right">
                          {change !== null && (
                            <span className={change > 0 ? 'text-red-600' : 'text-[#1a61e8]'}>
                              {change > 0 ? '+' : ''}{change}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
